import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Send, Copy, Trash2, Plus, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { MaterialSection } from "@/components/MaterialSection";
import { MaterialModal } from "@/components/MaterialModal";
import { GiveAccessSection } from "@/components/GiveAccessSection";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { CATEGORIES, fmtNumber, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const inputCls =
  "h-12 w-full rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const SAMPLE_TYPES = ["FIT SAMPLE", "PP SAMPLE", "TOP SAMPLE", "TEST SAMPLE"];
const SAMPLE_STATUSES = ["APPROVED", "PENDING", "REJECTED"];

const blankOrder = {
  buyer_name: "", department: "", style_number: "", season: "", order_qty: 0,
  delivery_date: "", remarks: "", po_details: [], materials: {}, published: false,
  status: "draft", progress: 0, access_user_ids: [], style: "", file_handover_date: "", sample_details: [],
};

function Labeled({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-slate-600">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function OrderEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();

  // persistedId tracks the real order id once created, so that create -> edit
  // always updates the same order even if the URL hasn't switched yet.
  const [savedId, setSavedId] = useState(id === "new" ? null : id);
  const persistedId = savedId || (id !== "new" ? id : null);
  const isNew = !persistedId;

  const [order, setOrder] = useState(blankOrder);
  const [loading, setLoading] = useState(id !== "new");
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [modal, setModal] = useState({ open: false, category: null, item: null });
  const [poModal, setPoModal] = useState({ open: false, po: null });
  const [sampleModal, setSampleModal] = useState({ open: false, sample: null });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [factoryUsers, setFactoryUsers] = useState([]);

  const canShare = ["senior_merchandiser", "merchandiser", "developer"].includes(user?.role);

  useEffect(() => {
    if (!canShare) return;
    api.get("/factory-users").then(({ data }) => setFactoryUsers(data)).catch(() => {});
  }, [canShare]);

  const load = useCallback(async () => {
    if (id === "new") {
      setReadOnly(user?.role === "owner");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder({ ...blankOrder, ...data });
      setSavedId(data.id);
      setReadOnly(!data.can_edit);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, user]);

  useEffect(() => {
    load();
  }, [load]);

  // scroll to a section when opened from a notification
  useEffect(() => {
    const section = params.get("section");
    if (section && !loading) {
      const el = document.querySelector(`[data-testid="material-section-${section}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [params, loading]);

  const set = (k, v) => setOrder((o) => ({ ...o, [k]: v }));

  const orderLabel = `${order.buyer_name || "Order"} · Style ${order.style_number || "—"} · Total order qty: ${fmtNumber(order.order_qty)} pcs`;

  const buildPayload = (published) => ({
    buyer_name: order.buyer_name,
    department: order.department,
    style: order.style,
    style_number: order.style_number,
    season: order.season,
    order_qty: Number(order.order_qty) || 0,
    delivery_date: order.delivery_date || null,
    file_handover_date: order.file_handover_date || null,
    remarks: order.remarks,
    po_details: order.po_details || [],
    sample_details: order.sample_details || [],
    materials: order.materials || {},
    access_user_ids: order.access_user_ids || [],
    published,
  });

  const save = async (published) => {
    if (!order.buyer_name.trim()) {
      toast.error("Buyer name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(published);
      let data;
      if (!persistedId) {
        ({ data } = await api.post("/orders", payload));
        setSavedId(data.id);
        setOrder({ ...blankOrder, ...data });
        window.history.replaceState(null, "", `/orders/${data.id}`);
        toast.success(published ? "Order published" : "Draft saved");
      } else {
        ({ data } = await api.put(`/orders/${persistedId}`, payload));
        setOrder({ ...blankOrder, ...data });
        toast.success(published ? "Order published" : "Draft saved");
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/orders/${persistedId}/duplicate`);
      toast.success("Order duplicated as a draft");
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/orders/${persistedId}`);
      toast.success("Order deleted");
      navigate("/");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  // ---- material handlers ----
  const saveMaterial = (payload) => {
    const cat = modal.category;
    setOrder((o) => {
      const list = [...(o.materials?.[cat] || [])];
      if (modal.item) {
        const idx = list.findIndex((x) => x.id === modal.item.id);
        if (idx >= 0) list[idx] = { ...payload, id: modal.item.id };
      } else {
        list.push({ ...payload, id: uid() });
      }
      return { ...o, materials: { ...o.materials, [cat]: list } };
    });
    setModal({ open: false, category: null, item: null });
  };

  const duplicateMaterial = (cat, item) => {
    setOrder((o) => {
      const list = [...(o.materials?.[cat] || []), { ...item, id: uid(), name: `${item.name} (Copy)` }];
      return { ...o, materials: { ...o.materials, [cat]: list } };
    });
    toast.success("Item duplicated");
  };

  const deleteMaterial = (cat, item) => {
    setOrder((o) => {
      const list = (o.materials?.[cat] || []).filter((x) => x.id !== item.id);
      return { ...o, materials: { ...o.materials, [cat]: list } };
    });
    toast.success("Item removed");
  };

  // ---- PO handlers ----
  const savePo = (po) => {
    setOrder((o) => {
      const list = [...(o.po_details || [])];
      if (po.id) {
        const idx = list.findIndex((x) => x.id === po.id);
        if (idx >= 0) list[idx] = po;
      } else {
        list.push({ ...po, id: uid() });
      }
      return { ...o, po_details: list };
    });
    setPoModal({ open: false, po: null });
  };

  const deletePo = (poId) => {
    setOrder((o) => ({ ...o, po_details: (o.po_details || []).filter((p) => p.id !== poId) }));
  };

  // ---- Sample handlers ----
  const saveSample = (sample) => {
    setOrder((o) => {
      const list = [...(o.sample_details || [])];
      if (sample.id) {
        const idx = list.findIndex((x) => x.id === sample.id);
        if (idx >= 0) list[idx] = sample;
      } else {
        list.push({ ...sample, id: uid() });
      }
      return { ...o, sample_details: list };
    });
    setSampleModal({ open: false, sample: null });
  };

  const deleteSample = (sampleId) => {
    setOrder((o) => ({ ...o, sample_details: (o.sample_details || []).filter((s) => s.id !== sampleId) }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <TopNav />
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  const poTotal = (order.po_details || []).reduce((s, p) => s + (Number(p.quantity) || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <TopNav />

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              data-testid="back-btn"
              onClick={() => navigate("/")}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">
                {isNew ? "Create new order" : `${order.buyer_name} · Style ${order.style_number}`}
              </h1>
              {!isNew && <p className="text-base text-slate-500">Created by {order.created_by_name}</p>}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <StatusBadge status={order.status} testId="order-header-status" />
            <div className="hidden w-40 sm:block">
              <div className="mb-1 flex justify-between text-sm text-slate-500">
                <span>Progress</span>
                <span className="font-bold text-slate-700">{order.progress || 0}%</span>
              </div>
              <ProgressBar value={order.progress} status={order.status} />
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {/* Basic Order Information */}
          <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-700">
              Basic Order Information
            </h3>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Labeled label="Buyer name" required>
                <input data-testid="order-buyer-input" disabled={readOnly} className={inputCls} value={order.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} placeholder="e.g. ZARA" />
              </Labeled>
              <Labeled label="Department">
                <input data-testid="order-department-input" disabled={readOnly} className={inputCls} value={order.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Menswear" />
              </Labeled>
              <Labeled label="Style">
                <input data-testid="order-style-name-input" disabled={readOnly} className={inputCls} value={order.style} onChange={(e) => set("style", e.target.value)} placeholder="e.g. T-Shirt, Hoodie, Cargo Pants" />
              </Labeled>
              <Labeled label="Style number">
                <input data-testid="order-style-input" disabled={readOnly} className={inputCls} value={order.style_number} onChange={(e) => set("style_number", e.target.value)} placeholder="e.g. 7545/500" />
              </Labeled>
              <Labeled label="Season">
                <input data-testid="order-season-input" disabled={readOnly} className={inputCls} value={order.season} onChange={(e) => set("season", e.target.value)} placeholder="e.g. Autumn/Winter 2026" />
              </Labeled>
              <Labeled label="Total order quantity (pcs)">
                <input data-testid="order-qty-input" disabled={readOnly} type="number" className={inputCls} value={order.order_qty} onChange={(e) => set("order_qty", e.target.value)} />
              </Labeled>
              <Labeled label="Delivery date">
                <input data-testid="order-delivery-input" disabled={readOnly} type="date" className={inputCls} value={(order.delivery_date || "").slice(0, 10)} onChange={(e) => set("delivery_date", e.target.value)} />
              </Labeled>
              <Labeled label="File handover date">
                <input data-testid="order-file-handover-input" disabled={readOnly} type="date" className={inputCls} value={(order.file_handover_date || "").slice(0, 10)} onChange={(e) => set("file_handover_date", e.target.value)} />
              </Labeled>
            </div>
            <div className="mt-5">
              <Labeled label="Remarks">
                <textarea disabled={readOnly} className="min-h-[70px] w-full rounded-lg border border-slate-200 bg-white p-3 text-base text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" value={order.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Optional notes…" />
              </Labeled>
            </div>
          </section>

          {/* PO Details */}
          <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-700">PO Details</h3>
              <span className="text-base text-slate-500">Total: <strong className="text-slate-700">{fmtNumber(poTotal)} pcs</strong></span>
            </div>
            {(order.po_details || []).length > 0 ? (
              <div className="mt-4 overflow-x-auto mf-scrollbar">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 text-sm uppercase tracking-wide text-slate-500">
                      <th className="rounded-l-lg px-4 py-3 font-semibold">PO number</th>
                      <th className="px-4 py-3 font-semibold">Colour</th>
                      <th className="px-4 py-3 font-semibold">Quantity</th>
                      <th className="px-4 py-3 font-semibold">Shipped Quantity</th>
                      <th className="px-4 py-3 font-semibold">Delivery date</th>
                      <th className="rounded-r-lg px-4 py-3 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.po_details.map((p) => (
                      <tr key={p.id} data-testid={`po-row-${p.id}`} className="border-t border-slate-100 text-base text-slate-700">
                        <td className="px-4 py-3 font-semibold text-slate-800">{p.po_number}</td>
                        <td className="px-4 py-3">{p.colour}</td>
                        <td className="px-4 py-3">{fmtNumber(p.quantity)}</td>
                        <td className="px-4 py-3">{fmtNumber(p.shipped_quantity)}</td>
                        <td className="px-4 py-3">{fmtDate(p.delivery_date)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {!readOnly && (
                              <>
                                <button data-testid={`po-edit-${p.id}`} onClick={() => setPoModal({ open: true, po: p })} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200">
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button data-testid={`po-delete-${p.id}`} onClick={() => deletePo(p.id)} className="flex h-8 w-8 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-base text-slate-400">No purchase orders added yet.</p>
            )}
            {!readOnly && (
              <button
                data-testid="add-po-btn"
                onClick={() => setPoModal({ open: true, po: null })}
                className="mt-4 inline-flex h-11 items-center gap-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50 px-5 text-base font-semibold text-blue-700 transition-colors hover:bg-blue-100"
              >
                <Plus className="h-5 w-5" /> Add PO
              </button>
            )}
          </section>

          {/* Sample Details */}
          <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-700">Sample Details</h3>
            </div>
            {(order.sample_details || []).length > 0 ? (
              <div className="mt-4 overflow-x-auto mf-scrollbar">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 text-sm uppercase tracking-wide text-slate-500">
                      <th className="rounded-l-lg px-4 py-3 font-semibold">Type of sample</th>
                      <th className="px-4 py-3 font-semibold">Sending date</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Approved date</th>
                      <th className="rounded-r-lg px-4 py-3 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.sample_details.map((s) => (
                      <tr key={s.id} data-testid={`sample-row-${s.id}`} className="border-t border-slate-100 text-base text-slate-700">
                        <td className="px-4 py-3 font-semibold text-slate-800">{s.sample_type}</td>
                        <td className="px-4 py-3">{fmtDate(s.sending_date)}</td>
                        <td className="px-4 py-3">{s.status}</td>
                        <td className="px-4 py-3">{fmtDate(s.approved_date)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {!readOnly && (
                              <>
                                <button data-testid={`sample-edit-${s.id}`} onClick={() => setSampleModal({ open: true, sample: s })} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200">
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button data-testid={`sample-delete-${s.id}`} onClick={() => deleteSample(s.id)} className="flex h-8 w-8 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-base text-slate-400">No samples added yet.</p>
            )}
            {!readOnly && (
              <button
                data-testid="add-sample-btn"
                onClick={() => setSampleModal({ open: true, sample: null })}
                className="mt-4 inline-flex h-11 items-center gap-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50 px-5 text-base font-semibold text-blue-700 transition-colors hover:bg-blue-100"
              >
                <Plus className="h-5 w-5" /> Add Sample
              </button>
            )}
          </section>

          {/* Material sections */}
          {CATEGORIES.map((c) => (
            <MaterialSection
              key={c.key}
              category={c.key}
              items={order.materials?.[c.key] || []}
              readOnly={readOnly}
              defaultOpen={c.key === "fabrication" || c.key === params.get("section")}
              onAdd={() => setModal({ open: true, category: c.key, item: null })}
              onEdit={(item) => setModal({ open: true, category: c.key, item })}
              onDuplicate={(item) => duplicateMaterial(c.key, item)}
              onDelete={(item) => deleteMaterial(c.key, item)}
            />
          ))}

          {/* Give Access (order sharing) */}
          {canShare && (
            <GiveAccessSection
              factoryUsers={factoryUsers}
              accessIds={order.access_user_ids || []}
              creatorId={order.created_by || user?.id}
              creatorName={order.created_by_name || user?.full_name}
              onChange={(ids) => set("access_user_ids", ids)}
              readOnly={readOnly}
            />
          )}
        </div>
      </main>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-end gap-3 px-4 py-3 sm:px-6">
          {readOnly ? (
            <span className="mr-auto text-base font-medium text-slate-500">Read-only view</span>
          ) : (
            <>
              {!isNew && (
                <>
                  <Button data-testid="duplicate-order-btn" variant="outline" className="h-12 gap-2 px-5" onClick={duplicate}>
                    <Copy className="h-5 w-5" /> Duplicate
                  </Button>
                  <Button data-testid="delete-order-btn" variant="outline" className="h-12 gap-2 border-rose-200 px-5 text-rose-600 hover:bg-rose-50" onClick={() => setConfirmDelete(true)}>
                    <Trash2 className="h-5 w-5" /> Delete
                  </Button>
                </>
              )}
              <Button data-testid="save-draft-btn" variant="outline" className="h-12 gap-2 px-5" disabled={saving} onClick={() => save(false)}>
                <Save className="h-5 w-5" /> Save Draft
              </Button>
              <Button data-testid="publish-order-btn" className="h-12 gap-2 bg-blue-600 px-6 hover:bg-blue-700" disabled={saving} onClick={() => save(true)}>
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Publish Order
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Material modal */}
      <MaterialModal
        open={modal.open}
        category={modal.category}
        item={modal.item}
        orderQty={order.order_qty}
        orderLabel={orderLabel}
        readOnly={readOnly}
        onClose={() => setModal({ open: false, category: null, item: null })}
        onSave={saveMaterial}
      />

      {/* PO modal */}
      <PoModal open={poModal.open} po={poModal.po} onClose={() => setPoModal({ open: false, po: null })} onSave={savePo} />
      <SampleModal open={sampleModal.open} sample={sampleModal.sample} onClose={() => setSampleModal({ open: false, sample: null })} onSave={saveSample} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-order-btn" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">
              Delete order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PoModal({ open, po, onClose, onSave }) {
  const [form, setForm] = useState({ po_number: "", colour: "", quantity: 0, shipped_quantity: 0, delivery_date: "" });
  useEffect(() => {
    if (open) setForm(po || { po_number: "", colour: "", quantity: 0, shipped_quantity: 0, delivery_date: "" });
  }, [open, po]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">{po ? "Edit PO" : "Add PO"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Labeled label="PO number" required>
            <input data-testid="po-number-input" className={inputCls} value={form.po_number} onChange={(e) => set("po_number", e.target.value)} placeholder="e.g. PO-88213" />
          </Labeled>
          <Labeled label="Colour">
            <input data-testid="po-colour-input" className={inputCls} value={form.colour} onChange={(e) => set("colour", e.target.value)} placeholder="e.g. Navy" />
          </Labeled>
          <Labeled label="Quantity">
            <input data-testid="po-qty-input" type="number" className={inputCls} value={form.quantity} onChange={(e) => set("quantity", Number(e.target.value))} />
          </Labeled>
          <Labeled label="Shipped quantity">
            <input data-testid="po-shipped-qty-input" type="number" className={inputCls} value={form.shipped_quantity} onChange={(e) => set("shipped_quantity", Number(e.target.value))} />
          </Labeled>
          <Labeled label="Delivery date">
            <input data-testid="po-delivery-input" type="date" className={inputCls} value={(form.delivery_date || "").slice(0, 10)} onChange={(e) => set("delivery_date", e.target.value)} />
          </Labeled>
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-11 px-6" onClick={onClose}>Cancel</Button>
          <Button data-testid="po-save-btn" className="h-11 bg-blue-600 px-6 hover:bg-blue-700" onClick={() => onSave(form)}>
            {po ? "Save" : "Add PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SampleModal({ open, sample, onClose, onSave }) {
  const empty = { sample_type: "FIT SAMPLE", sending_date: "", status: "PENDING", approved_date: "" };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (open) setForm(sample || empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sample]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">{sample ? "Edit Sample" : "Add Sample"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Labeled label="Type of sample">
            <Select value={form.sample_type} onValueChange={(v) => set("sample_type", v)}>
              <SelectTrigger data-testid="sample-type-select" className="h-12 text-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SAMPLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Labeled>
          <Labeled label="Sending date">
            <input data-testid="sample-sending-date-input" type="date" className={inputCls} value={(form.sending_date || "").slice(0, 10)} onChange={(e) => set("sending_date", e.target.value)} />
          </Labeled>
          <Labeled label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="sample-status-select" className="h-12 text-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SAMPLE_STATUSES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Labeled>
          <Labeled label="Approved date">
            <input data-testid="sample-approved-date-input" type="date" className={inputCls} value={(form.approved_date || "").slice(0, 10)} onChange={(e) => set("approved_date", e.target.value)} />
          </Labeled>
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-11 px-6" onClick={onClose}>Cancel</Button>
          <Button data-testid="sample-save-btn" className="h-11 bg-blue-600 px-6 hover:bg-blue-700" onClick={() => onSave(form)}>
            {sample ? "Save" : "Add Sample"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
